import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, OnDestroy } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Subscription } from 'rxjs';
import { PREVIEW_CASES, TemplateStatus } from 'src/app/core/constants/common';
import { PLACEHOLDERS } from 'src/app/modules/segments/interfaces/create-segment-new.constant';
import {
  ChannelJson,
  Section,
} from 'src/app/modules/segments/interfaces/create-segment-new.interface';
import { ItemplateContentResponse } from 'src/app/modules/templates/interfaces/create-template.interface';
import {
  ITemplateFilteredData,
  ITemplateListResponse,
} from 'src/app/modules/templates/interfaces/template-list-response.interface';
import { TemplateService } from 'src/app/modules/templates/services/template.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-new-placeholder-mapping',
  templateUrl: './new-placeholder-mapping.component.html',
  styleUrls: ['./new-placeholder-mapping.component.scss'],
})
export class NewPlaceholderMappingComponent implements OnInit, OnDestroy {
  channelJsons: ChannelJson[] = [];
  channel: string;
  selectedChannels: string[] = [];
  channelForms: FormGroup[] = [];

  activeChannel = 0;
  activeSection = 0;
  completedSections: { [channelId: number]: Set<number> } = {};
  touchedChannels: Set<number> = new Set([0]); // Initialize with first channel as touched
  placeholderConstant = PLACEHOLDERS;
  selectedPushOption: string | null = null;
  existingMappings: any[] = [];
  editMode = false;
  previewApiErrorMap: Record<string, string> = {};
  previewApiLoadingMap: Record<string, boolean> = {};
  templateDataMap: Record<string, ITemplateFilteredData> = {};
  highlightedContentMap: Record<string, string> = {};
  whatsAppCarouselHighlightedContentMap: Record<string, string[]> = {};
  rcsCarouselHighlightedContentMap: Record<string, string[]> = {};
  emailHtmlContentMap: Record<string, string> = {};
  // Stores the original (un-highlighted) email templateJson and subject per templateCode
  rawEmailDataMap: Record<string, { templateJson: any; subject: string }> = {};
  // Stores original (un-highlighted) template data for non-email channels (for button label re-highlighting)
  rawTemplateDataMap: Record<string, any> = {};
  previewCase = PREVIEW_CASES;

  activeFocusPlaceholder: string | null = null;
  private subscriptions: Subscription[] = [];

  setActiveFocusPlaceholder(key: string | null): void {
    this.activeFocusPlaceholder = key;
  }

  constructor(
    private readonly config: DynamicDialogConfig,
    public readonly dialogRef: DynamicDialogRef,
    private readonly fb: FormBuilder,
    private readonly templateService: TemplateService,
  ) {
    this.channelJsons = this.config.data.placeholdersArray;
    this.selectedChannels = this.config.data.selectedChannels;
    this.existingMappings = this.config.data.existingMappings || [];
    this.editMode = this.existingMappings.length > 0;
  }

  get activeSectionJson(): Section {
    return this.channelJsons[this.activeChannel].sections[this.activeSection];
  }

  get isFormSectionActive(): boolean {
    const key = this.activeSectionJson?.sectionKey;
    return (
      key === this.placeholderConstant.FORM ||
      key === this.placeholderConstant.WEB_FORM_CTA
    );
  }

  get activeFormCode(): string {
    const ch = this.channelJsons[this.activeChannel];
    if (!ch) return '';
    const templateCode = ch.templateCode;
    const tData = this.templateDataMap[templateCode];
    if (!tData) return '';
    // Check WhatsApp buttons
    const waButtons = tData.whatsAppTemplates?.[0]?.buttons || [];
    const waForm = waButtons.find((b) =>
      (b.buttonAction || '').toUpperCase().includes('WEB_FORM'),
    );
    if (waForm?.formCode) return waForm.formCode;
    // Check RCS buttons
    const rcsButtons = tData.rcsTemplates?.[0]?.buttons || [];
    const rcsForm = rcsButtons.find((b) =>
      (b.buttonAction || '').toUpperCase().includes('WEB_FORM'),
    );
    if (rcsForm?.formCode) return rcsForm.formCode;
    // Check SMS
    if (tData.smsTemplate?.formCode) return tData.smsTemplate.formCode;
    // Check Email — form code is in button properties' selectedWebForm field
    const emailRows: any[] = (tData as any)?.templateJson?.layout?.rows || [];
    for (const row of emailRows) {
      for (const col of row.columns || []) {
        const content = col.content;
        if (content?.type === 'BUTTON' && content?.properties?.selectedWebForm) {
          const parts = content.properties.selectedWebForm.split('|');
          return parts[parts.length - 1];
        }
        // Also check text content for attached form
        if (content?.type === 'TEXT' && content?.properties?.attachedFormCode) {
          return content.properties.attachedFormCode;
        }
      }
    }
    return '';
  }

  get activePreviewChannel(): string {
    return this.channelJsons[this.activeChannel]?.channelType || '';
  }

  get activeTemplateCode(): string {
    return this.channelJsons[this.activeChannel]?.templateCode || '';
  }

  isSelected(ph: any): boolean {
    const section = this.channelJsons[this.activeChannel].sections.find(
      (sec) => sec.sectionKey === 'pushRecipient',
    );
    return (
      this.channelForms[this.activeChannel]?.get(
        section?.placeholders?.[0]?.controlKey !== undefined
          ? this.channelForms[this.activeChannel]?.get(
              section.placeholders[0].controlKey,
            )?.value
          : undefined,
      ) === ph.key
    );
  }

  getFormControl(key: string): FormControl {
    return this.channelForms[this.activeChannel].get(key) as FormControl;
  }

  ngOnInit(): void {
    this.initializeForms();
    this.prefillExistingValues();
    this.loadTemplatePreviews();
  }

  initializeForms(): void {
    this.channelJsons.forEach((channel, id) => {
      const group = this.fb.group({});
      const pushSection = channel.sections.find(
        (s) => s.sectionKey === 'pushRecipient',
      );
      if (pushSection && pushSection.placeholders.length > 0) {
        this.selectedPushOption = pushSection.placeholders[0].key;
      }
      channel.sections.forEach((section) => {
        if (section.sectionKey === this.placeholderConstant.CARD) {
          section?.cards?.forEach((card) => {
            card.groups.forEach((groupItem) => {
              groupItem.placeholders.forEach((ph) => {
                group.addControl(
                  ph?.controlKey || '',
                  this.fb.control(
                    { value: ph.value || '', disabled: ph.disabled },
                    Validators.required,
                  ),
                );
              });
            });
          });
        } else if (section.sectionKey === 'pushRecipient') {
          const pushControl = this.fb.control(null); // For radio group value
          group.addControl('pushRecipient', pushControl);

          // Add value controls for options that need input
          section.placeholders.forEach((ph) => {
            if (ph.needsInput) {
              group.addControl(
                ph.controlKey,
                this.fb.control('', ph.needsInput ? Validators.required : null),
              );
            }
          });
        } else {
          section.placeholders.forEach((ph) => {
            group.addControl(
              ph.controlKey,
              this.fb.control(
                { value: ph.value, disabled: ph.disabled },
                Validators.required,
              ),
            );
          });
        }
      });
      this.channelForms.push(group);
      this.completedSections[id] = new Set<number>();
    });
  }

  // Navigate channels
  onChannelSelect(id: number): void {
    // Allow navigation if channel is completed, touched, or is the next sequential channel
    if (
      this.channelComplete(id) ||
      this.touchedChannels.has(id) ||
      id === this.activeChannel + 1 ||
      id < this.activeChannel
    ) {
      this.activeChannel = id;
      this.activeSection = 0;
      this.activeFocusPlaceholder = null; // reset focus highlight
      // Mark the channel as touched
      this.touchedChannels.add(id);
    }
  }

  // Navigate sections
  onSectionSelect(j: number) {
    if (
      j <= this.activeSection ||
      this.isSectionComplete(this.activeChannel, j)
    ) {
      this.activeSection = j;
      this.activeFocusPlaceholder = null; // reset focus highlight
    }
  }

  isSectionValid(channelIdx: number, sectionIdx: number): boolean {
    const form = this.channelForms[channelIdx];
    const section = this.channelJsons[channelIdx].sections[sectionIdx];

    if (section.sectionKey === this.placeholderConstant.ADDITIONAL_PARAM) {
      if (!section.isVisibleOption) {
        return true;
      }
      if (!section.selectedKey) {
        return false;
      }

      if (
        this.channelForms[this.activeChannel].get(section.selectedKey.key)
          ?.value === ''
      ) {
        return false;
      }

      return true;
    }

    if (section.sectionKey === this.placeholderConstant.CARD) {
      return (
        section.cards?.every((card) =>
          card.groups.every((group) =>
            group.placeholders.every((ph) => !!form.get(ph.controlKey)?.value),
          ),
        ) ?? false
      );
    }

    return section.placeholders.every((ph) => !!form.get(ph.controlKey)?.value);
  }

  isPushRecipientValid(): boolean {
    const section = this.channelJsons[this.activeChannel].sections.find(
      (sec) => sec.sectionKey === 'pushRecipient',
    );

    if (!section) return false;

    if (!this.selectedPushOption) return false;

    const selectedPlaceholder = section.placeholders.find(
      (ph) => ph.key === this.selectedPushOption,
    );

    if (selectedPlaceholder?.needsInput) {
      const value = this.channelForms[this.activeChannel].get(
        selectedPlaceholder.controlKey,
      )?.value;

      return !!value && value.trim() !== '';
    }

    return true;
  }

  onPreviousChannel(event: Event) {
    event.stopPropagation();
    if (this.activeChannel > 0) {
      this.activeChannel--;
      this.activeSection = 0;
      this.activeFocusPlaceholder = null; // reset focus highlight
      // Mark the previous channel as touched when moving to it
      this.touchedChannels.add(this.activeChannel);
      this.updateHighlightedContent(this.activeChannel);
    }
  }

  // Check if section is complete
  isSectionComplete(channelId: number, sectionId: number): boolean {
    if (this.editMode) return true;
    return this.completedSections[channelId]?.has(sectionId) || false;
  }

  // Check if channel is complete (all sections mapped)
  channelComplete(idx: number): boolean {
    if (this.editMode) {
      return true;
    }
    return this.channelJsons[idx].sections.every((_, sIdx) =>
      this.isSectionComplete(idx, sIdx),
    );
  }

  // Check if channel has been touched (visited)
  isChannelTouched(idx: number): boolean {
    return this.touchedChannels.has(idx);
  }

  // Check if channel is clickable (completed, touched, or immediately next to active)
  isChannelClickable(idx: number): boolean {
    return this.channelComplete(idx) || this.isChannelTouched(idx);
  }

  isSectionActive(channelId: number, sectionId: number): boolean {
    return this.activeChannel === channelId && this.activeSection === sectionId;
  }

  // Navigate sections within a channel
  onNextSection(event: Event) {
    event.stopPropagation();
    this.activeFocusPlaceholder = null; // reset focus highlight
    const cIdx = this.activeChannel;
    const secIdx = this.activeSection;
    const currentSection = this.channelJsons[cIdx].sections[secIdx];

    // 1) Mark this section complete
    if (this.isSectionValid(cIdx, this.activeSection)) {
      this.completedSections[cIdx].add(this.activeSection);
    }
    if (currentSection.copyKey) {
      this.syncSharedSection(currentSection.sectionKey, cIdx);
    }
    // Re-apply highlights with cleared focus before navigating
    this.updateHighlightedContent(cIdx);
    const sections = this.channelJsons[cIdx].sections;
    if (this.activeSection < sections.length - 1) {
      this.activeSection++;
    } else if (
      this.channelComplete(cIdx) &&
      cIdx < this.channelJsons.length - 1
    ) {
      this.activeChannel++;
      this.activeSection = 0;
      // Mark the new channel as touched when moving to it
      this.touchedChannels.add(this.activeChannel);
    }
  }

  syncSharedSection(sectionKey: string, ownerIdx: number) {
    const ownerGroup = this.channelForms[ownerIdx];
    const ownerSection = this.channelJsons[ownerIdx].sections.find(
      (s) => s.sectionKey === sectionKey,
    );
    if (!ownerSection?.copyKey) return;
    const copyKey = ownerSection.copyKey;

    // 1. Handle intra-channel copying first (same channel)
    this.handleIntraChannelCopying(ownerIdx, ownerSection, ownerGroup, copyKey);

    // 2. Original inter-channel copying
    this.handleInterChannelCopying(ownerIdx, ownerSection, ownerGroup, copyKey);
  }

  private handleIntraChannelCopying(
    ownerIdx: number,
    ownerSection: Section,
    ownerGroup: FormGroup,
    copyKey: string,
  ) {
    const ownerChannel = this.channelJsons[ownerIdx];

    // Find all sections in same channel with same copyKey (except owner section)
    ownerChannel.sections.forEach((depSection, depIdx) => {
      if (
        depSection.copyKey === copyKey &&
        depSection.sectionKey !== ownerSection.sectionKey
      ) {
        this.copyPlaceholders(
          ownerSection,
          ownerGroup,
          depSection,
          this.channelForms[ownerIdx],
        );
      }
    });
  }

  private handleInterChannelCopying(
    ownerIdx: number,
    ownerSection: Section,
    ownerGroup: FormGroup,
    copyKey: string,
  ) {
    for (let idx = ownerIdx + 1; idx < this.channelJsons.length; idx++) {
      const depChannel = this.channelJsons[idx];
      const depSection = depChannel.sections.find((s) => s.copyKey === copyKey);
      if (!depSection) continue;

      this.copyPlaceholders(
        ownerSection,
        ownerGroup,
        depSection,
        this.channelForms[idx],
      );
    }
  }

  private copyPlaceholders(
    ownerSection: Section,
    ownerGroup: FormGroup,
    depSection: Section,
    depGroup: FormGroup,
  ) {
    depSection.placeholders.forEach((depPh) => {
      // Find matching placeholder by key in owner section
      const ownerPh = ownerSection.placeholders?.find(
        (p) => p.key === depPh.key,
      );

      if (ownerPh) {
        const ownerControl = ownerGroup.get(ownerPh.controlKey);
        if (ownerControl) {
          const depControl = depGroup.get(depPh.controlKey);
          if (depControl) {
            // Copy value and disable control
            depControl.setValue(ownerControl.value, { emitEvent: false });
            depControl.disable({ onlySelf: true, emitEvent: false });
            depPh.disabled = true; // Update UI state
          }
        }
      }
    });
  }

  collapseCard(cardNum: number): void {
    const section = this.activeSectionJson;
    section?.cards?.forEach((card) => {
      if (Number(card.sequence) === cardNum) {
        card.isVisible = !card.isVisible;
      } else {
        card.isVisible = false;
      }
    });
  }

  onPrevSection(event: Event) {
    event.stopPropagation();
    this.activeFocusPlaceholder = null; // reset focus highlight
    if (this.activeSection > 0) {
      this.activeSection--;
    } else if (this.activeChannel > 0) {
      this.activeChannel--;
      const prevSections = this.channelJsons[this.activeChannel].sections;
      this.activeSection = prevSections.length - 1;
    }
    this.updateHighlightedContent(this.activeChannel);
  }

  onChangeToggle(): void {
    const section = this.activeSectionJson;
    if (section.sectionKey === this.placeholderConstant.ADDITIONAL_PARAM) {
      section.selectedKey = null; // Reset key selection

      if (!section.isVisibleOption) {
        if (!this.completedSections[this.activeChannel]) {
          this.completedSections[this.activeChannel] = new Set();
        }
        this.completedSections[this.activeChannel].add(this.activeSection);
      } else {
        // If toggle is on, mark section as incomplete
        this.completedSections[this.activeChannel]?.delete(this.activeSection);
      }
    }
  }

  onFinish(event: Event) {
    event.stopPropagation();
    // Build final segmentMapper payload
    const segmentMapper: Array<any> = [];
    const emailSubjectKeysMap: Record<number, Set<string>> = {};

    this.channelJsons.forEach((ch, chIdx) => {
      const form = this.channelForms[chIdx];
      if (ch.channelType === 'Email') {
        const subj = ch.sections.find(
          (s) => s.sectionKey === this.placeholderConstant.SUBJECT,
        );
        emailSubjectKeysMap[chIdx] = new Set(
          subj?.placeholders.map((ph) => ph.key) || [],
        );
      }
      if (ch.channelType !== 'Recipient ID') {
        ch.sections.forEach((sec) => {
          if (
            sec.sectionKey === this.placeholderConstant.TEMPLATE &&
            ch.channelType === 'Email'
          ) {
            sec.placeholders.forEach((ph, idx) => {
              if (emailSubjectKeysMap[chIdx].has(ph.key)) {
                return; // skip duplicates
              }

              const val = form.get(ph.controlKey)?.value;
              if (val) {
                segmentMapper.push({
                  templateCode: ch.templateCode,
                  type: ph.type,
                  fileColumnName: val,
                  parentType: ph.parentType,
                  sequence: idx.toString(),
                  placeHolderValue: `#${ph.key}#`,
                });
              }
            });
            return;
          }

          if (sec.sectionKey === 'card' && sec.cards) {
            sec.cards.forEach((card: any) => {
              card.groups.forEach((grp: any) => {
                grp.placeholders.forEach((ph: any) => {
                  segmentMapper.push({
                    templateCode: ch.templateCode,
                    type: ph.type,
                    fileColumnName: form.get(ph.controlKey)?.value,
                    parentType: ph.parentType,
                    sequence: `${card.sequence}`,
                    placeHolderValue: `#${ph.key}#`,
                  });
                });
              });
            });
          }

          if (sec.sectionKey === this.placeholderConstant.ADDITIONAL_PARAM) {
            if (!sec.isVisibleOption) return;
            const sel = sec.selectedKey;
            if (sel) {
              const ctrlName = sel.controlKey ?? sel.key;
              const val = form.get(ctrlName)?.value;
              if (val) {
                segmentMapper.push({
                  templateCode: ch.templateCode,
                  type: sel.type || 'Push_Additional_Param',
                  fileColumnName: val,
                  parentType: sel.parentType,
                  sequence: '0',
                  placeHolderValue: `#${sel.key}#`,
                });
              }
            }
            return;
          }

          if (sec.placeholders && sec.placeholders.length > 0) {
            const phList = sec.placeholders;
            phList.forEach((ph: any, index: number) => {
              const value = form.get(ph.controlKey)?.value;
              if (value) {
                segmentMapper.push({
                  templateCode: ch.templateCode,
                  type: ph.type,
                  fileColumnName: value,
                  parentType: ph.parentType,
                  sequence: index?.toString(),
                  placeHolderValue: `#${ph.key}#`,
                });
              }
            });
          }
        });
        return;
      }
      // Process recipient channel
      ch.sections.forEach((sec) => {
        // Fallback sections (SMS/WhatsApp/RCS)
        if (sec.sectionKey === 'fallbackRecipient') {
          const value = form.get(sec.placeholders[0].controlKey)?.value;
          if (value && sec.fallbackTemplates) {
            sec.fallbackTemplates.forEach((fbTemplate) => {
              segmentMapper.push({
                templateCode: fbTemplate.templateCode,
                type: 'RecipientId',
                fileColumnName: value,
                parentType: fbTemplate.parentType,
                sequence: '0',
                placeHolderValue: `#${sec.placeholders[0].key}#`,
              });
            });
          }
        }
        // Push notification section
        if (sec.sectionKey === 'pushRecipient') {
          if (!this.selectedPushOption) return;
          const selectedPlaceholder = sec.placeholders.find(
            (ph) => ph.key === this.selectedPushOption,
          );
          if (!selectedPlaceholder) return;
          let fileColumnName = '';
          let actualKey = selectedPlaceholder.key;

          if (selectedPlaceholder.key === 'fallbackRecipient') {
            // Find first fallback section
            const fallbackSection = ch.sections.find(
              (s) => s.sectionKey === 'fallbackRecipient',
            );

            if (fallbackSection) {
              const realPh = fallbackSection.placeholders.find(
                (ph) => ph.key !== 'fallbackRecipient',
              );
              if (realPh) {
                actualKey = selectedPlaceholder.fallback_key || realPh.key;
                fileColumnName = form.get(realPh.controlKey)?.value;
              }
            }
            // Custom option with input
            else if (selectedPlaceholder.needsInput) {
              fileColumnName = form.get(selectedPlaceholder.controlKey)?.value;
            }

            if (fileColumnName) {
              segmentMapper.push({
                templateCode: sec.templateCode,
                type: 'Recipient_Push',
                fileColumnName: fileColumnName,
                parentType: selectedPlaceholder.parentType,
                sequence: '0',
                placeHolderValue: `#${actualKey}#`,
              });
            }
          } else {
            fileColumnName = form.get(selectedPlaceholder.controlKey)?.value;
            if (fileColumnName) {
              segmentMapper.push({
                templateCode: sec.templateCode,
                type: 'Recipient_Push',
                fileColumnName: fileColumnName,
                parentType: selectedPlaceholder.parentType,
                sequence: '0',
                placeHolderValue: `#${actualKey}#`,
              });
            }
          }
        }

        // Email section
        if (sec.sectionKey === 'emailRecipient') {
          sec.placeholders.forEach((ph: any) => {
            const value = form.get(ph.controlKey)?.value;
            segmentMapper.push({
              templateCode: sec.templateCode,
              type: ph.type,
              fileColumnName: value,
              parentType: ph.parentType,
              sequence: '0',
              placeHolderValue: `#${ph.key}#`,
            });
          });
        }
      });
    });

    this.dialogRef.close({
      segmentMapper,
      isValid: true,
    });
  }

  onKeyValueChange(event: any) {
    const section = this.activeSectionJson;
    if (!section.selectedKey) return;
    if (
      !this.channelForms[this.activeChannel].get(section.selectedKey.controlKey)
    ) {
      this.channelForms[this.activeChannel].addControl(
        section.selectedKey.controlKey,
        new FormControl('', Validators.required),
      );
    }
  }

  prefillExistingValues() {
    if (!this.existingMappings.length) return;

    this.channelJsons.forEach((channel, chIdx) => {
      const form = this.channelForms[chIdx];

      // 1. Prefill non-recipient channels
      if (channel.channelType !== 'Recipient ID') {
        channel.sections.forEach((section) => {
          if (section.sectionKey === 'card' && section.cards) {
            section.cards.forEach((card) => {
              card.groups.forEach((group) => {
                group.placeholders.forEach((ph) => {
                  const mapping = this.findMapping(
                    channel.templateCode,
                    `#${ph.key}#`,
                    card.sequence.toString(),
                  );
                  if (mapping) {
                    form.get(ph.controlKey)?.setValue(mapping.fileColumnName);
                  }
                });
              });
            });
          } else if (section.placeholders) {
            section.placeholders.forEach((ph) => {
              const mapping = this.findMapping(
                channel.templateCode,
                `#${ph.key}#`,
              );
              if (mapping) {
                form.get(ph.controlKey)?.setValue(mapping.fileColumnName);
              }
            });
          }
          if (
            section.sectionKey === this.placeholderConstant.ADDITIONAL_PARAM
          ) {
            for (const opt of section.labelOptions || []) {
              const mapping = this.findMapping(
                channel.templateCode,
                `#${opt.key}#`,
              );
              if (mapping) {
                // set selectedKey to this option (so UI shows it)
                section.selectedKey = opt;
                section.isVisibleOption = true;
                // ensure the control exists
                const ctrlName = opt.key;
                if (!form.get(ctrlName)) {
                  form.addControl(
                    ctrlName,
                    this.fb.control(
                      mapping.fileColumnName,
                      Validators.required,
                    ),
                  );
                } else {
                  form.get(ctrlName)?.setValue(mapping.fileColumnName);
                }
                break; // use first found mapping
              }
            }
          }
        });
      }
      // 2. Prefill recipient channel
      else {
        channel.sections.forEach((section) => {
          // Fallback sections (SMS/WhatsApp/RCS)
          if (
            section.sectionKey === 'fallbackRecipient' &&
            section.fallbackTemplates
          ) {
            section.placeholders.forEach((ph) => {
              // Check each fallback template for existing mappings
              for (const fbTemplate of section.fallbackTemplates ?? []) {
                const mapping = this.findMapping(
                  fbTemplate.templateCode,
                  `#${ph.key}#`,
                );

                if (mapping) {
                  form.get(ph.controlKey)?.setValue(mapping.fileColumnName);
                  break; // Use first found mapping
                }
              }
            });
          }

          // Push notification section
          if (section.sectionKey === 'pushRecipient') {
            section.placeholders.forEach((ph) => {
              // Handle "Same As" option
              if (ph.key === 'fallbackRecipient') {
                // Check if "Same As" was used
                const sameAsMapping = this.existingMappings.find(
                  (m) =>
                    m.templateCode === section.templateCode &&
                    m.placeHolderValue !== `#${ph.key}#` &&
                    m.type === 'Recipient_Push',
                );

                if (sameAsMapping) {
                  this.selectedPushOption = ph.key;
                }
              }
              // Handle custom options
              else if (ph.needsInput) {
                const mapping = this.findMapping(
                  section.templateCode || '',
                  `#${ph.key}#`,
                );

                if (mapping) {
                  form.get(ph.controlKey)?.setValue(mapping.fileColumnName);
                  this.selectedPushOption = ph.key;
                }
              }
            });
          }

          // Email section
          if (section.sectionKey === 'emailRecipient') {
            section.placeholders.forEach((ph) => {
              const mapping = this.findMapping(
                section.templateCode || '',
                `#${ph.key}#`,
              );
              if (mapping) {
                form.get(ph.controlKey)?.setValue(mapping.fileColumnName);
              }
            });
          }
        });
      }
    });
  }

  private findMapping(
    templateCode: string,
    placeHolderValue: string,
    sequence?: string,
  ) {
    return this.existingMappings.find(
      (m) =>
        m.templateCode === templateCode &&
        m.placeHolderValue === placeHolderValue &&
        (!sequence || m.sequence === sequence),
    );
  }

  isRecipientFormValid(): boolean {
    // 1. Check if pushRecipient section exists and validate if present
    const pushSection = this.channelJsons[this.activeChannel]?.sections.find(
      (s) => s.sectionKey === 'pushRecipient',
    );
    if (pushSection && !this.isPushRecipientValid()) {
      return false; // Invalid if section present but validation fails
    }

    // 2. Fallback validation remains unchanged
    const fallbackSection = this.channelJsons[
      this.activeChannel
    ]?.sections.find((s) => s.sectionKey === 'fallbackRecipient');
    if (fallbackSection) {
      const fallbackPh = fallbackSection.placeholders[0];
      if (
        !this.channelForms[this.activeChannel].get(fallbackPh.controlKey)?.value
      ) {
        return false;
      }
    }

    // 3. Email validation remains unchanged
    const emailSection = this.channelJsons[this.activeChannel]?.sections.find(
      (s) => s.sectionKey === 'emailRecipient',
    );
    if (emailSection) {
      for (const ph of emailSection.placeholders) {
        if (!this.channelForms[this.activeChannel].get(ph.controlKey)?.value) {
          return false;
        }
      }
    }

    return true; // All applicable validations passed
  }

  loadTemplatePreviews(): void {
    this.channelJsons.forEach((ch, idx) => {
      if (ch.channelType !== 'Recipient ID') {
        this.fetchTemplateData(ch.channelType, ch.templateCode, idx);
      }
      const sub = this.channelForms[idx].valueChanges.subscribe(() => {
        this.updateHighlightedContent(idx);
      });
      this.subscriptions.push(sub);
    });
  }

  fetchTemplateData(
    channelType: string,
    templateCode: string,
    channelIdx: number,
  ): void {
    if (channelType === 'Email') {
      if (!templateCode) return;
      this.previewApiLoadingMap[templateCode] = true;
      delete this.previewApiErrorMap[templateCode];

      this.templateService.getEmailemplateContent(templateCode).subscribe({
        next: (response: ItemplateContentResponse) => {
          if (response.data?.htmlContent) {
            this.emailHtmlContentMap[templateCode] = response.data.htmlContent;

            if (response.data.templateJson) {
              const parseTemplateJson = JSON.parse(response.data.templateJson);
              this.templateDataMap[templateCode] = {
                ...(this.templateDataMap[templateCode] || {}),
                templateJson: parseTemplateJson,
                subject: response.data.subject || '',
              } as any;
              // Store raw originals so highlights can be re-applied cleanly
              this.rawEmailDataMap[templateCode] = {
                templateJson: JSON.parse(JSON.stringify(parseTemplateJson)),
                subject: response.data.subject || '',
              };
            }
            this.updateHighlightedContent(channelIdx);
          }
          this.previewApiLoadingMap[templateCode] = false;
        },
        error: (error: HttpErrorResponse) => {
          this.previewApiErrorMap[templateCode] =
            this.getApiErrorMessage(error);
          this.previewApiLoadingMap[templateCode] = false;
        },
      });
    } else {
      if (!templateCode) return;
      this.previewApiLoadingMap[templateCode] = true;
      delete this.previewApiErrorMap[templateCode];
      const payload = {
        filterValue: templateCode,
        pageNumber: 0,
        pageSize: 10,
        filterType: 'Template ID',
        status: [TemplateStatus.PUBLISHED],
        channel: this.normalizeChannelToPreviewCase(channelType),
      };

      this.templateService.getTemplateListing(payload).subscribe({
        next: (response: ITemplateListResponse) => {
          if (response.data && response.data.filteredResponse.length > 0) {
            const previewChannel =
              this.normalizeChannelToPreviewCase(channelType);
            const match =
              response.data.filteredResponse.find(
                (t) =>
                  t.channel?.toUpperCase?.() === channelType.toUpperCase() ||
                  this.normalizeChannelToPreviewCase(t.channel || '') ===
                    previewChannel,
              ) || response.data.filteredResponse[0];

            if (match) {
              this.templateDataMap[templateCode] = match;
              // Store raw copy for button label re-highlighting
              this.rawTemplateDataMap[templateCode] = structuredClone(match);
              this.updateHighlightedContent(channelIdx);
            }
          }
          this.previewApiLoadingMap[templateCode] = false;
        },
        error: (error: HttpErrorResponse) => {
          this.previewApiErrorMap[templateCode] =
            this.getApiErrorMessage(error);
          this.previewApiLoadingMap[templateCode] = false;
        },
      });
    }
  }

  private getApiErrorMessage(error: any): string {
    return (
      error?.error?.errors?.[0]?.errorMessage ||
      error?.error?.message ||
      error?.message ||
      'Failed to load template preview. Please try again.'
    );
  }

  normalizeChannelToPreviewCase(channelType: string): string {
    const normalized = (channelType || '').trim().toUpperCase();

    if (normalized === 'WHATSAPP') return this.previewCase.WHATSAPP;
    if (normalized === 'SMS') return this.previewCase.SMS;
    if (normalized === 'RCS') return this.previewCase.RCS;
    if (normalized === 'EMAIL') return this.previewCase.EMAIL;
    if (normalized === 'PUSH NOTIFICATION') {
      return this.previewCase.PUSH_NOTIFICATION;
    }
    return channelType;
  }

  updateHighlightedContent(channelIdx: number): void {
    const ch = this.channelJsons[channelIdx];
    const templateCode = ch.templateCode;
    if (!templateCode) return;
    const form = this.channelForms[channelIdx];
    const formValues = form.getRawValue();
    const previewChannel = this.normalizeChannelToPreviewCase(ch.channelType);

    // Collect ALL placeholders across all sections
    const placeholderMap: Record<string, string> = {};
    ch.sections.forEach((sec) => {
      // General placeholders in the section
      if (sec.placeholders && Array.isArray(sec.placeholders)) {
        sec.placeholders.forEach((ph) => {
          placeholderMap[ph.key] = formValues[ph.controlKey] || '';
        });
      }

      if (
        sec.sectionKey === this.placeholderConstant.ADDITIONAL_PARAM &&
        sec.selectedKey
      ) {
        placeholderMap[sec.selectedKey.key] =
          formValues[sec.selectedKey.key] || '';
      }
    });

    // Get form name/code for form URL interpolation
    const tData = this.templateDataMap[templateCode];
    const formCode =
      tData?.whatsAppTemplates?.[0]?.formCode ||
      tData?.rcsTemplates?.[0]?.formCode ||
      tData?.smsTemplate?.formCode ||
      '';
    const formName =
      tData?.whatsAppTemplates?.[0]?.formName ||
      tData?.rcsTemplates?.[0]?.formName ||
      tData?.smsTemplate?.formName ||
      '';

    if (previewChannel === this.previewCase.EMAIL) {
      if (tData) {
        // Use raw originals to avoid double-highlighting on repeated calls
        const rawData = this.rawEmailDataMap[templateCode];
        const rawTemplateJson = rawData?.templateJson;
        const rawSubject = rawData?.subject ?? tData.subject ?? '';

        const updatedTemplateJson = this.applyHighlightsToEmailJson(
          rawTemplateJson,
          placeholderMap,
        );
        const highlightedSubject = this.applyHighlights(
          rawSubject,
          placeholderMap,
        );

        this.templateDataMap[templateCode] = {
          ...tData,
          templateJson: updatedTemplateJson,
          subject: highlightedSubject,
        } as any;
        // Trigger change detection by reassigning the map
        this.templateDataMap = { ...this.templateDataMap };
      } else {
        const rawHtml = this.emailHtmlContentMap[templateCode];
        if (rawHtml) {
          this.highlightedContentMap[templateCode] = this.applyHighlights(
            rawHtml,
            placeholderMap,
          );
        }
      }
      return;
    }

    if (previewChannel === this.previewCase.WHATSAPP && tData) {
      const rawTpl = this.rawTemplateDataMap[templateCode] || tData;
      const cards = rawTpl.whatsAppTemplates || [];
      const first = cards[0];
      const firstType = (first?.type || '').toUpperCase();
      const isCarousel =
        firstType === 'CAROUSEL_CARD' || firstType === 'CAROUSEL_BUBBLE';

      if (isCarousel) {
        // Highlight the CAROUSEL_BUBBLE body text (shown above the cards)
        const bubble = cards.find(
          (c: any) => (c?.type || '').toUpperCase() === 'CAROUSEL_BUBBLE',
        );
        if (bubble) {
          let body = bubble.content || '';
          body = this.applyHighlights(body, placeholderMap);
          body = this.interpolateFormUrl(body, formCode, formName);
          this.highlightedContentMap[templateCode] = body;
        }
        // Highlight individual carousel card contents
        const carouselCards = cards.filter(
          (c: any) => (c?.type || '').toUpperCase() === 'CAROUSEL_CARD',
        );
        this.updateCarouselHighlights(
          channelIdx,
          templateCode,
          carouselCards,
          'whatsapp',
        );
      } else {
        let body = first?.content || '';
        body = this.applyHighlights(body, placeholderMap);
        body = this.interpolateFormUrl(body, formCode, formName);
        this.highlightedContentMap[templateCode] = body;
      }
      // Apply highlights to button labels
      this.applyButtonLabelHighlights(
        templateCode,
        rawTpl,
        placeholderMap,
        'whatsapp',
      );
      return;
    }

    if (previewChannel === this.previewCase.RCS && tData) {
      const rawTpl = this.rawTemplateDataMap[templateCode] || tData;
      const cards = rawTpl.rcsTemplates || [];
      const first = cards[0];
      const isCarousel = first?.type?.toUpperCase() === 'CAROUSEL_CARD';

      if (isCarousel) {
        this.updateCarouselHighlights(channelIdx, templateCode, cards, 'rcs');
      } else {
        // Highlight Body
        let body = first?.content || '';
        body = this.applyHighlights(body, placeholderMap);
        body = this.interpolateFormUrl(body, formCode, formName);
        this.highlightedContentMap[templateCode] = body;

        // Highlight Title (Standalone)
        if (first?.title) {
          first.title = this.applyHighlights(
            this.rawTemplateDataMap[templateCode].rcsTemplates[0].title,
            placeholderMap,
          );
        }
      }
      // Apply highlights to button labels
      this.applyButtonLabelHighlights(
        templateCode,
        rawTpl,
        placeholderMap,
        'rcs',
      );
      return;
    }

    if (previewChannel === this.previewCase.SMS && tData) {
      let body = tData.smsTemplate?.content || '';
      body = this.applyHighlights(body, placeholderMap);
      body = this.interpolateFormUrl(body, formCode, formName);
      this.highlightedContentMap[templateCode] = body;
      return;
    }

    if (previewChannel === this.previewCase.PUSH_NOTIFICATION && tData) {
      let body = tData.pushNotificationTemplate?.content || '';
      body = this.applyHighlights(body, placeholderMap);
      this.highlightedContentMap[templateCode] = body;
      return;
    }
  }

  private updateCarouselHighlights(
    channelIdx: number,
    templateCode: string,
    cards: any[],
    type: 'whatsapp' | 'rcs',
  ): void {
    const ch = this.channelJsons[channelIdx];
    const form = this.channelForms[channelIdx];
    const formValues = form.getRawValue();
    const cardSection = ch.sections.find(
      (s) => s.sectionKey === this.placeholderConstant.CARD,
    );

    const highlightedCards: string[] = cards.map((card, idx) => {
      let content = card.content || '';
      const cardData = cardSection?.cards?.find(
        (c) => Number(c.sequence) === idx,
      );
      if (cardData) {
        const phMap: Record<string, string> = {};
        cardData.groups.forEach((g) => {
          g.placeholders.forEach((ph) => {
            if (
              ph.type?.toLowerCase() === 'placeholder' ||
              ph.type?.toLowerCase() === 'text'
            ) {
              phMap[ph.key] = formValues[ph.controlKey] || '';
            }
          });
        });
        content = this.applyHighlights(content, phMap);

        // Highlight Title (Carousel Card)
        if (card.title) {
          const rawTitle =
            this.rawTemplateDataMap[templateCode].rcsTemplates[idx]?.title ||
            card.title;
          card.title = this.applyHighlights(rawTitle, phMap);
        }
      }
      return content;
    });

    if (type === 'whatsapp') {
      this.whatsAppCarouselHighlightedContentMap[templateCode] =
        highlightedCards;
    } else {
      this.rcsCarouselHighlightedContentMap[templateCode] = highlightedCards;
    }
  }

  private applyHighlights(
    content: string,
    phMap: Record<string, string>,
  ): string {
    if (!content) return '';
    // Highlight states:
    // - Unmapped, not focused  → class="unmapped"              (grey/plain)
    // - Unmapped, focused      → class="mapped-highlight"      (blue — user is selecting)
    // - Mapped, not focused    → class="mapped-highlight focus-highlight" (yellow — value shown)
    // - Mapped, focused        → class="mapped-highlight"      (blue — re-selecting)
    return content.replace(/#([^#]+)#/g, (_match, key) => {
      const mappedValue = phMap[key];
      const isFocused = this.activeFocusPlaceholder === key;

      const hasMappedValue =
        mappedValue !== undefined && mappedValue !== null && mappedValue !== '';

      if (hasMappedValue) {
        const cls = isFocused
          ? 'mapped-highlight'
          : 'mapped-highlight focus-highlight';
        return `<span class="${cls}" title="Mapped to: ${mappedValue}">${mappedValue}</span>`;
      }

      if (isFocused) {
        return `<span class="mapped-highlight">#${key}#</span>`;
      }

      // Unmapped and not focused — plain unmapped only, no mapped-highlight
      return `<span class="unmapped">#${key}#</span>`;
    });
  }

  private interpolateFormUrl(
    content: string,
    formCode: string,
    formName: string,
  ): string {
    if (!formCode || !formName) return content;
    const formUrl = `<a href="${environment.FORM_PREVIEW_URL}/form/${formCode}/PREVIEW" target="_blank">${formName} (${formCode})</a>`;
    return content
      .replace('#form_url#', formUrl)
      .replace('#form_url_tiny#', formUrl);
  }

  /**
   * Deep-clone the template data, apply placeholder highlights to button
   * labels and mark buttons whose URL/value placeholders are mapped/focused.
   */
  // ── Button action → channelJson sectionKey mapping ───────────────────────
  private readonly BTN_ACTION_TO_SECTION_KEY: Record<string, string> = {
    DYNAMIC_CTA: 'dynamicCTA',
    DYNAMIC_URL: 'dynamicCTA',
    OPEN_URL: 'dynamicCTA',
    WEB_FORM_CTA: 'webFormCTA',
    WEB_FORM_URL: 'webFormCTA',
    CALL_BACK: 'callback',
    STATIC_CALLBACK: 'callback',
    DIAL_PHONE_NUMBER: 'callback',
    DIAL_ACTION: 'callback',
    URL: 'url',
  };

  /**
   * Resolves the channelJson sectionKey for a button based on its action type.
   * Returns null when the action has no corresponding CTA section.
   */
  private resolveBtnSectionKey(btnAction: string): string | null {
    const action = btnAction.toUpperCase().replace(/ /g, '_');
    return (
      this.BTN_ACTION_TO_SECTION_KEY[action] ||
      (action.includes('WEB_FORM') ? 'form' : null)
    );
  }

  /**
   * Fuzzy-matches a placeholder key against an expected section key.
   * Handles dotted / indexed paths (e.g. "cards[0].dynamicCTA1") by checking
   * the last segment both verbatim and with a trailing digit stripped.
   */
  private phKeyMatchesSectionKey(phKey: string | null | undefined, sectionKey: string): boolean {
    if (!phKey) return false;
    if (phKey === sectionKey) return true;
    const lastSegment = phKey.split(/[\.\[\]\_]/).filter(Boolean).pop() ?? '';
    return lastSegment === sectionKey || lastSegment.replace(/\d+$/, '') === sectionKey;
  }

  /**
   * Finds the nth placeholder (sorted by sequence) in the matching section of
   * the channelJson for this templateCode.  Returns null when not found.
   */
  private findSectionPlaceholder(
    templateCode: string,
    sectionKey: string,
    buttonIndex: number,
  ): any | null {
    const channel = this.channelJsons.find((ch) => ch.templateCode === templateCode);
    const section = channel?.sections.find((s: any) => s.sectionKey === sectionKey);
    if (!section) return null;
    const sorted = [...(section.placeholders as any[])].sort(
      (a, b) => (a.sequence ?? 0) - (b.sequence ?? 0),
    );
    return sorted[buttonIndex] ?? null;
  }

  /**
   * Deep-clone the template data, apply placeholder highlights to button
   * labels and mark buttons whose URL/value placeholders are mapped/focused.
   */
  private applyButtonLabelHighlights(
    templateCode: string,
    rawTpl: any,
    phMap: Record<string, string>,
    channel: 'whatsapp' | 'rcs',
  ): void {
    const cloned = JSON.parse(JSON.stringify(rawTpl));
    const templates: any[] =
      channel === 'whatsapp'
        ? cloned.whatsAppTemplates || []
        : cloned.rcsTemplates || [];

    // Tracks how many buttons of each sectionKey type we've seen so we can
    // do 1:1 positional matching: button[N] ↔ placeholder[N] in the section.
    const sectionKeyButtonCount: Record<string, number> = {};

    templates.forEach((tpl: any) => {
      (tpl.buttons || tpl.buttonRequests || []).forEach((btn: any) => {
        // ── Highlight label text ──────────────────────────────────────────
        ['buttonLabel', 'label', 'displayText'].forEach((field) => {
          if (btn[field]) btn[field] = this.applyHighlights(btn[field], phMap);
        });

        // ── Extract #placeholder# keys from URL/value fields ─────────────
        const tokenKeys: string[] = [
          btn.buttonValue, btn.url, btn.baseUrl, btn.callbackUrl,
          btn.dynamicUrl, btn.value, btn.actionUrl, btn.dialPhoneNumber,
        ]
          .filter(Boolean)
          .join(' ')
          .match(/#([^#]+)#/g)
          ?.map((m) => m.replace(/#/g, '')) ?? [];

        let isFocused = tokenKeys.some((k) => k === this.activeFocusPlaceholder);
        let isMapped  = tokenKeys.some((k) => !!phMap[k]);

        // ── Section-key resolution ────────────────────────────────────────
        const btnAction  = (btn.buttonAction || btn.type || '').toString();
        const sectionKey = this.resolveBtnSectionKey(btnAction);
        const btnIdx     = sectionKey ? (sectionKeyButtonCount[sectionKey] ?? 0) : -1;
        if (sectionKey) sectionKeyButtonCount[sectionKey] = btnIdx + 1;

        if (sectionKey && (!isFocused || !isMapped)) {
          // ① Fuzzy name-based check (handles "dynamicCTA2" → "dynamicCTA")
          const focusMatchesSectionKey = this.phKeyMatchesSectionKey(this.activeFocusPlaceholder, sectionKey);
          const isFormAlias = ['form', 'webFormCTA'].includes(sectionKey) &&
            this.phKeyMatchesSectionKey(this.activeFocusPlaceholder, sectionKey === 'form' ? 'webFormCTA' : 'form');

          if (!isFocused) isFocused = focusMatchesSectionKey || isFormAlias;

          const anyPhMapped = (key: string) =>
            Object.keys(phMap).some((k) => this.phKeyMatchesSectionKey(k, key) && !!phMap[k]);
          if (!isMapped) isMapped = anyPhMapped(sectionKey);
          if (!isMapped && ['form', 'webFormCTA'].includes(sectionKey)) {
            isMapped = anyPhMapped('form') || anyPhMapped('webFormCTA');
          }

          // ② Index-based fallback: raw ph.key ("cta1") lives in the section
          //    and doesn't match the section name ("dynamicCTA") at all.
          //    Map this button's position → the same-position placeholder.
          if (!isFocused || !isMapped) {
            const matchingPh = this.findSectionPlaceholder(templateCode, sectionKey, btnIdx);
            if (matchingPh) {
              if (!isFocused) isFocused = this.activeFocusPlaceholder === matchingPh.key;
              if (!isMapped)  isMapped  = !!phMap[matchingPh.key];
            }
          }
        }

        btn._highlightState = isFocused ? 'focused' : isMapped ? 'mapped' : 'none';
      });
    });

    this.templateDataMap[templateCode] = {
      ...(this.templateDataMap[templateCode] || {}),
      ...(channel === 'whatsapp'
        ? { whatsAppTemplates: cloned.whatsAppTemplates }
        : { rcsTemplates: cloned.rcsTemplates }),
    };
    this.templateDataMap = { ...this.templateDataMap };
  }

  /**
   * Deep-clone the email templateJson and apply placeholder highlights
   * to all TEXT htmlText fields and BUTTON text fields.
   */
  private applyHighlightsToEmailJson(
    templateJson: any,
    phMap: Record<string, string>,
  ): any {
    if (!templateJson) return templateJson;
    const json = structuredClone(templateJson); // deep clone
    const rows: any[] = json?.layout?.rows || [];
    rows.forEach((row: any) => {
      (row.columns || []).forEach((col: any) => {
        const content = col.content;
        if (!content) return;
        if (content.type === 'TEXT' && content.properties?.htmlText) {
          content.properties.htmlText = this.applyHighlights(
            content.properties.htmlText,
            phMap,
          );
        }
        if (content.type === 'BUTTON' && content.properties) {
          if (content.properties.text) {
            content.properties.text = this.applyHighlights(
              content.properties.text,
              phMap,
            );
          }

          // Compute _highlightState for Email CTAs
          const p = content.properties;
          const valueFields = [p.actionUrl, p.selectedWebForm, p.paramPlaceholder]
            .filter(Boolean)
            .join(' ');
          
          let isFocused = false;
          let isMapped = false;
          
          const placeholderKeys = (valueFields.match(/#([^#]+)#/g) || []).map(
            (m: string) => m.replace(/#/g, ''),
          );
          if (placeholderKeys.some((k) => this.activeFocusPlaceholder === k)) isFocused = true;
          if (placeholderKeys.some((k) => phMap[k] && phMap[k] !== '')) isMapped = true;

          // For web forms in email
          if (p.selectedWebForm || p.attachedFormCode) {
             if (this.activeFocusPlaceholder === 'form' || this.activeFocusPlaceholder === 'webFormCTA') isFocused = true;
             if (phMap['form'] || phMap['webFormCTA']) isMapped = true;
          }
          
          // For URLs in email
          if (p.actionUrl && !p.selectedWebForm && p.actionUrl.includes('{{')) {
             if (this.activeFocusPlaceholder === 'url') isFocused = true;
             if (phMap['url']) isMapped = true;
          }

          p._highlightState = isFocused ? 'focused' : isMapped ? 'mapped' : 'none';
        }
      });
    });
    // Also apply to footer
    if (json?.layout?.footer) {
      json.layout.footer = this.applyHighlights(json.layout.footer, phMap);
    }
    return json;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
